import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import SearchForm from "../../../component/search-form";
import CheckboxFilter from "../../../component/Filter/CheckboxFilter";
import SimpleBarReact from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import useDebounce from "../../../Hook/useDebounce";
import ComponentCard from "./ComponentCard";
import { sanitizeInput } from "../../../utils/index"

function ComponentList({
  setModalOpen,
  componentParameter,
  setDropdownOptions,
  setComponentParameter,
  setShowWatermark,
  downloadComponentModalOpen,
  setDownloadComponentModalOpen,
}) {
  const { componentListData } = useSelector((state) => state.manageComponent);

  const [searchText, setSearchText] = useState();
  const [searchData, setSearchData] = useState([]);
  /* ------------------- Checkbox Data ----------------- */
  const [checkboxData, setCheckboxData] = useState({
    "Component Type": [
      {
        label: "Text",
        value: "text",
        checked: false,
      },
      {
        label: "Number",
        value: "number",
        checked: false,
      },
      {
        label: "Date/Time",
        checked: false,
        value: "date/time",
      },
      {
        label: "Dropdown",
        checked: false,
        value: "dropdown",
      },
      {
        label: "Checkbox",
        checked: false,
        value: "check box",
      },
      {
        label: "Paragraph",
        checked: false,
        value: "paragraph",
      },
      {
        label: "Link",
        checked: false,
        value: "link",
      },
      {
        label: "Radio Group",
        checked: false,
        value: "radio group",
      },
      {
        label: "Toggle",
        checked: false,
        value: "toggle",
      },
    ],
  });

  // Use the debounce hook to get the debounced version of the handleSearch function
  const debouncedHandleSearch = useDebounce(searchText, 300);

  const handleCheckboxChange = (index, key) => {
    const updatedData = { ...checkboxData };
    updatedData[key][index].checked = !updatedData[key][index].checked;
    setCheckboxData(updatedData);
  };

  const handleSearchAndFilter = (searchQuery, listData) => {
    if (listData.length > 0 && searchQuery) {
      const searchResult = listData.filter((obj) =>
        obj.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchData(searchResult);
    } else {
      setSearchData(listData);
    }
  };

  useEffect(() => {
    const filteredData = componentListData.filter((item) => {
      const typeChecked = checkboxData["Component Type"].some(
        (checkbox) => checkbox.value === item.type && checkbox.checked
      );
      return typeChecked;
    });

    if (filteredData.length > 0) {
      handleSearchAndFilter(debouncedHandleSearch, filteredData);
    } else if (
      checkboxData["Component Type"].some((checkbox) => checkbox.checked)
    ) {
      handleSearchAndFilter(debouncedHandleSearch, []);
    } else {
      handleSearchAndFilter(debouncedHandleSearch, componentListData);
    }
  }, [debouncedHandleSearch, checkboxData, componentListData]);

  // Function to get different icon
  const getIcon = (type, isChecked) => {
    const iconMapping = {
      text: "fas fa-text",
      dropdown: "fas fa-caret-square-down",
      toggle: "fas fa-toggle-on",
      "date/time": "far fa-calendar",
      "check box": "fas fa-check-square",
      link: "fas fa-link",
      "radio group": "fa-solid fa-circle-dot",
      paragraph: "fa-solid fa-p",
      number: "fa-solid fa-n",
    };

    const iconClass = iconMapping[type.toLowerCase()];

    if (iconClass) {
      return (
        <span className={`${iconClass} ${isChecked && "text-white"}`}></span>
      );
    } else {
      return null;
    }
  };

  const getComponentData = (item) => {
    let componentObj = {
      componentType: item.type,
      componentId: item.component_id,
      componentName: item.label,
      componentPlaceholder: item.placeholder,
      componentTooltip: item.tooltip,
      isCheckedByDefault: item.is_check_by_default,
      validation: item.validation_api_id,
      minimumValue: item.min,
      maximumValue: item.max,
      component_special_id: item.component_name,
      priorDate: item.prior_date,
      futureDate: item.future_date,
      link_url: item.url,
      previewValue: "",
      is_immutable: item.is_immutable,
    };

    if(item?.list_selected_value) {
      componentObj['listSelectedValue'] = {
        label: item.list_selected_value,
        value: item.list_selected_value
      }
    }
    setComponentParameter(componentObj);
    setShowWatermark(false);
    setDropdownOptions(
      item.list_values
        ? JSON.parse(item.list_values).map((item) => {
            const obj = {
              label: item,
              value: item,
            };
            return obj;
          })
        : []
    );
  };

  const renderComponentCards = (data) => {
    return data.length > 0 ? (
      data.map((item, index) => (
        <ComponentCard
          key={index}
          index={index}
          item={item}
          isSelected={item.component_id === componentParameter.componentId}
          getComponentData={getComponentData}
          getIcon={getIcon}
        />
      ))
    ) : (
      <h2 className="text-center font-semibold">No results found</h2>
    );
  };

  return (
    <div className="h-screen" style={{ maxHeight: "calc(100vh - 107px)" }}>
      <h4 className="text-sm leading-3 tracking-tight font-bold text-[#535860] pb-1 border-b border-[#D9D9D9]">
        All Components
      </h4>
      <div className="flex items-center space-x-2 py-2 mt-1">
        <SearchForm
          placeholder="Search"
          formClass="w-full"
          onChange={(e) => {
            const value = sanitizeInput(e.target.value);
            setSearchText(value);
          }}
          value={searchText}
        />
        <CheckboxFilter
          checkboxData={checkboxData}
          handleCheckboxChange={handleCheckboxChange}
        />
      </div>
      <div className="pb-4 space-y-1">
        <button
          className="btn bg-[--bg-color-500] text-white text-sm leading-3 tracking-tight w-full"
          onClick={() => {
            setModalOpen(true);
            setComponentParameter({
              componentType: "",
              componentId: "",
              componentName: "",
              componentPlaceholder: "",
              componentTooltip: "",
              isCheckedByDefault: false,
              validation: [],
              minimumValue: "",
              maximumValue: "",
              priorDate: true,
              futureDate: true,
              link_url: "",
              component_special_id: "",
              previewValue: ""
            });
            setDropdownOptions([{ label: null, value: null }]);
          }}
        >
          + Create New Component
        </button>
        <button
          className="btn border-slate-200 hover:border-slate-300 bg-white text-[#1E293B] text-sm leading-3 tracking-tight w-full"
          onClick={() => {setDownloadComponentModalOpen(!downloadComponentModalOpen)}}
        >
          Download Existing Components
        </button>
      </div>
      <SimpleBarReact style={{ maxHeight: "calc(100vh - 300px)" }}>
        <div className="mt-1 space-y-2 overflow-x-hidden">
          {renderComponentCards(searchData)}
        </div>
      </SimpleBarReact>
    </div>
  );
}

export default ComponentList;
